import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LayoutFrame, ListItem, ListTemplate, SearchBar, registerHomeTile } from '@room/shared-ui';
import { useToast } from '@room/shared-ui/components/UI';
import { Badge } from '@room/shared-ui/components/ui/badge';
import { Button } from '@room/shared-ui/components/ui/button';
import { adminAPI } from '../../../lib/api';
import styles from '../AdminPage.module.css';
import { adminTileVisible } from '../homeTiles';

type SystemUser = {
  id: string;
  username?: string;
  role?: string;
};

type DevConsoleSettings = {
  globalEnabled: boolean;
  overrides: Record<string, boolean>;
};

registerHomeTile({
  id: 'home-admin-dev-console',
  label: 'Dev Console',
  icon: '🧰',
  route: '/admin/dev-console',
  section: 'main',
  order: 100,
  visible: adminTileVisible,
});

const roleBadgeVariant = (role?: string) => {
  if (!role) return 'outline';
  if (role.includes('super')) return 'info';
  if (role.includes('system')) return 'success';
  return 'secondary';
};

export default function DevConsolePage() {
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [globalEnabled, setGlobalEnabled] = useState(false);
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});

  const settingsQuery = useQuery({
    queryKey: ['admin', 'dev-console-settings'],
    queryFn: () => adminAPI.getDevConsoleSettings(),
  });

  const usersQuery = useQuery({
    queryKey: ['admin', 'system-users'],
    queryFn: () => adminAPI.getSystemUsers(),
  });

  const settings = (settingsQuery.data?.data ?? null) as DevConsoleSettings | null;
  const users = (usersQuery.data?.data?.users ?? []) as SystemUser[];

  useEffect(() => {
    if (settings) {
      setGlobalEnabled(settings.globalEnabled ?? false);
      setOverrides(settings.overrides ?? {});
    }
  }, [settings]);

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;
    return users.filter((user) => {
      const haystack = [user.username, user.id, user.role].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }, [search, users]);

  const mutation = useMutation({
    mutationFn: () => adminAPI.saveDevConsoleSettings({ globalEnabled, overrides }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'dev-console-settings'] });
      addToast('Developer console access updated.', 'success');
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Failed to update dev console access.';
      addToast(message, 'error');
    },
  });

  const resolveOverrideValue = (userId: string) => {
    if (userId in overrides) {
      return overrides[userId] ? 'allow' : 'deny';
    }
    return 'inherit';
  };

  const handleOverrideChange = (userId: string, value: string) => {
    setOverrides((prev) => {
      const next = { ...prev };
      if (value === 'inherit') {
        delete next[userId];
      } else {
        next[userId] = value === 'allow';
      }
      return next;
    });
  };

  return (
    <LayoutFrame withSection={false}>
      <ListTemplate<SystemUser>
        items={filteredUsers}
        loading={settingsQuery.isLoading || usersQuery.isLoading}
        error={settingsQuery.error instanceof Error ? settingsQuery.error.message : null}
        keyExtractor={(item) => item.id}
        useAppHeader
        appHeader={{
          title: 'Developer Console Access',
          subtitle: 'Control who can open the developer console experience.',
          actions: (
            <Button size="sm" onClick={() => mutation.mutate()} loading={mutation.isPending}>
              Save
            </Button>
          ),
        }}
        search={
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div>
                <h3 className={styles.sectionTitle}>Global access</h3>
                <p className={`${styles.sectionSubtitle} content-wrap`}>
                  Enable the dev console for all users unless overridden.
                </p>
              </div>
              <label className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={globalEnabled}
                  onChange={() => setGlobalEnabled((prev) => !prev)}
                />
                Enabled
              </label>
            </div>
            <SearchBar placeholder="Search users..." onSearch={setSearch} />
          </div>
        }
        renderItem={(user) => {
          const role = user.role ?? 'user';
          return (
            <ListItem
              title={user.username || user.id}
              subtitle={`User ID: ${user.id}`}
              badges={<Badge variant={roleBadgeVariant(role)}>{role}</Badge>}
              trailing={
                <select
                  className={styles.selectInput}
                  value={resolveOverrideValue(user.id)}
                  onChange={(event) => handleOverrideChange(user.id, event.target.value)}
                >
                  <option value="inherit">Use global</option>
                  <option value="allow">Allow</option>
                  <option value="deny">Deny</option>
                </select>
              }
            />
          );
        }}
      />
    </LayoutFrame>
  );
}
